import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, X, FileDown, Filter, AlertCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Maintenance cockpit — tasks catalogue + completion log + PDF report.
 *
 * Tasks tab:  CRUD of maintenance tasks. Each row shows the worst-status
 *             roll-up across machines as a coloured badge, with a tooltip
 *             listing per-machine status.
 * Logs tab:   chronological history of completions with filters
 *             (machine / task / operator / date range), Owner-only delete.
 * PDF report: GET /api/myroast/maintenance/report.pdf — opens in new tab
 *             with the current filter window.
 */

const FREQUENCY_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "per_roast", label: "Per roast" },
  { value: "every_n_roasts", label: "Every N roasts" },
  { value: "every_n_hours", label: "Every N hours" },
];

const CATEGORY_OPTIONS = [
  { value: "cleaning", label: "Cleaning" },
  { value: "inspection", label: "Inspection" },
  { value: "service", label: "Service" },
  { value: "safety", label: "Safety" },
];

// Severity ordering for "worst status" rollup
const STATUS_RANK = { overdue: 4, due: 3, upcoming: 2, ok: 1, never: 0 };
const STATUS_STYLES = {
  overdue:  { label: "Overdue",  cls: "bg-destructive/15 text-destructive border-destructive/30" },
  due:      { label: "Due",      cls: "bg-orange-100 text-orange-800 border-orange-300" },
  upcoming: { label: "Upcoming", cls: "bg-yellow-100 text-yellow-800 border-yellow-300" },
  ok:       { label: "On track", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  never:    { label: "Not yet",  cls: "bg-muted text-muted-foreground border-border" },
};

function rollupStatus(statusByMachine) {
  const entries = Object.values(statusByMachine || {});
  if (entries.length === 0) return "never";
  let worst = "never";
  for (const e of entries) {
    if ((STATUS_RANK[e.status] ?? 0) > (STATUS_RANK[worst] ?? 0)) worst = e.status;
  }
  return worst;
}

function formatFrequency(t) {
  const kind = FREQUENCY_OPTIONS.find((o) => o.value === t.frequency_kind)?.label || t.frequency_kind;
  if (t.frequency_value && (t.frequency_kind === "every_n_roasts" || t.frequency_kind === "every_n_hours")) {
    return `${kind.replace("Every N", "Every")} ${t.frequency_value}`;
  }
  return kind;
}

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function StatusBadge({ status, title }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.never;
  return (
    <span
      title={title}
      className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border ${s.cls}`}
      data-testid={`status-${status}`}
    >
      {s.label}
    </span>
  );
}

export default function Maintenance() {
  const { user } = useAuth();
  const isOwner = user?.role === "Owner";
  const canWrite = ["Owner", "ProductionManager"].includes(user?.role);
  const [tab, setTab] = useState("tasks"); // tasks | logs
  const [machines, setMachines] = useState([]);
  const [operators, setOperators] = useState([]);

  // Load master data once for badge tooltips + log column labels
  useEffect(() => {
    api.get("/machines").then((r) => setMachines(r.data || [])).catch(() => {});
    api.get("/operators").then((r) => setOperators(r.data || [])).catch(() => {});
  }, []);

  const machineNameById = useMemo(() => {
    const m = {};
    machines.forEach((x) => { m[x.id] = x.name; });
    return m;
  }, [machines]);
  const operatorNameById = useMemo(() => {
    const m = {};
    operators.forEach((x) => {
      m[x.id] = [x.name, x.surname].filter(Boolean).join(" ") || x.name || "";
    });
    return m;
  }, [operators]);

  return (
    <div data-testid="maintenance-page">
      <PageHeader
        eyebrow="Operations"
        title="Maintenance"
        description="Cleaning, service and inspection tasks for the roastery floor. Operators log completions from the tablet — this is your cockpit."
        testId="maintenance-header"
        actions={<DownloadReport machines={machines} />}
      />

      <div className="flex gap-1 mb-5 border-b border-border" role="tablist">
        <TabBtn id="tasks" tab={tab} setTab={setTab}>Tasks</TabBtn>
        <TabBtn id="logs" tab={tab} setTab={setTab}>Logs</TabBtn>
      </div>

      {tab === "tasks" && (
        <TasksPane
          canWrite={canWrite}
          isOwner={isOwner}
          machines={machines}
          machineNameById={machineNameById}
        />
      )}
      {tab === "logs" && (
        <LogsPane
          isOwner={isOwner}
          machines={machines}
          operators={operators}
          machineNameById={machineNameById}
          operatorNameById={operatorNameById}
        />
      )}
    </div>
  );
}

function TabBtn({ id, tab, setTab, children }) {
  const active = tab === id;
  return (
    <button
      role="tab"
      onClick={() => setTab(id)}
      data-testid={`maintenance-tab-${id}`}
      className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors
                  ${active
                    ? "border-primary text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground"}`}
    >
      {children}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
// Tasks tab
// ─────────────────────────────────────────────────────────────
function TasksPane({ canWrite, isOwner, machines, machineNameById }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null); // task object | "new" | null

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/myroast/maintenance/tasks", {
        params: { include_inactive: true },
      });
      setTasks(data || []);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onDelete = async (task) => {
    if (!window.confirm(`Delete "${task.name}"? Past log entries are preserved.`)) return;
    try {
      await api.delete(`/myroast/maintenance/tasks/${task.id}`);
      await load();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-muted-foreground">
          {loading ? "Loading…" : `${tasks.length} task${tasks.length === 1 ? "" : "s"}`}
        </p>
        {canWrite && (
          <button
            onClick={() => setEditing("new")}
            data-testid="maintenance-new-task"
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-semibold hover:bg-[hsl(20,41%,32%)]"
          >
            <Plus className="w-4 h-4" /> New task
          </button>
        )}
      </div>

      {error && <ErrorBanner message={error} />}

      <div className="bg-card border border-border rounded overflow-x-auto">
        <table className="w-full text-sm" data-testid="maintenance-tasks-table">
          <thead>
            <tr className="bg-secondary">
              <th className="label-tiny px-4 py-3 text-left">Task</th>
              <th className="label-tiny px-4 py-3 text-left">Category</th>
              <th className="label-tiny px-4 py-3 text-left">Frequency</th>
              <th className="label-tiny px-4 py-3 text-left">Applies to</th>
              <th className="label-tiny px-4 py-3 text-left">Status</th>
              {canWrite && <th className="w-24" />}
            </tr>
          </thead>
          <tbody>
            {!loading && tasks.length === 0 && (
              <tr>
                <td colSpan={canWrite ? 6 : 5} className="px-4 py-12 text-center text-muted-foreground">
                  <p className="mb-1">No maintenance tasks yet.</p>
                  <p className="text-xs">Add tasks like &quot;Clean drum&quot; or &quot;Replace filter&quot; so operators can log them from the tablet.</p>
                </td>
              </tr>
            )}
            {tasks.map((t) => {
              const status = rollupStatus(t.status_by_machine);
              const machinesList = Object.entries(t.status_by_machine || {})
                .map(([mid, v]) => `${machineNameById[mid] || mid}: ${v.status}${v.last_completed_at ? ` (${formatDateTime(v.last_completed_at)})` : ""}`)
                .join("\n");
              return (
                <tr
                  key={t.id}
                  className={`border-t border-border hover:bg-secondary/40 ${t.is_active === false ? "opacity-50" : ""}`}
                  data-testid={`maintenance-task-row-${t.id}`}
                >
                  <td className="px-4 py-2.5">
                    <div className="font-medium">{t.name}</div>
                    {t.description && (
                      <div className="text-xs text-muted-foreground mt-0.5">{t.description}</div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 capitalize">{t.category || "—"}</td>
                  <td className="px-4 py-2.5">
                    {formatFrequency(t)}
                    {t.estimated_minutes ? (
                      <div className="text-xs text-muted-foreground">{t.estimated_minutes} min</div>
                    ) : null}
                  </td>
                  <td className="px-4 py-2.5 text-sm">
                    {t.applies_to_machine_id
                      ? (machineNameById[t.applies_to_machine_id] || "—")
                      : <span className="text-muted-foreground">All machines</span>}
                  </td>
                  <td className="px-4 py-2.5">
                    <StatusBadge status={status} title={machinesList || "No completions yet"} />
                  </td>
                  {canWrite && (
                    <td className="px-2 py-2 text-right whitespace-nowrap">
                      <button
                        onClick={() => setEditing(t)}
                        data-testid={`maintenance-task-edit-${t.id}`}
                        className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-primary"
                        title="Edit"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      {isOwner && (
                        <button
                          onClick={() => onDelete(t)}
                          data-testid={`maintenance-task-delete-${t.id}`}
                          className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing && (
        <TaskEditor
          task={editing === "new" ? null : editing}
          machines={machines}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </>
  );
}

function TaskEditor({ task, machines, onClose, onSaved }) {
  const blank = {
    name: "",
    description: "",
    category: "cleaning",
    frequency_kind: "weekly",
    frequency_value: "",
    estimated_minutes: "",
    applies_to_machine_id: "",
    is_active: true,
  };
  const [form, setForm] = useState(() => task
    ? { ...blank, ...task,
        frequency_value: task.frequency_value ?? "",
        estimated_minutes: task.estimated_minutes ?? "",
        applies_to_machine_id: task.applies_to_machine_id ?? "" }
    : blank);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const usesValue = form.frequency_kind === "every_n_roasts" || form.frequency_kind === "every_n_hours";

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    const payload = {
      name: form.name.trim(),
      description: form.description?.trim() || null,
      category: form.category,
      frequency_kind: form.frequency_kind,
      frequency_value: usesValue && form.frequency_value !== ""
        ? parseInt(form.frequency_value, 10) : null,
      estimated_minutes: form.estimated_minutes !== ""
        ? parseInt(form.estimated_minutes, 10) : null,
      applies_to_machine_id: form.applies_to_machine_id || null,
      is_active: !!form.is_active,
    };
    try {
      if (task) {
        await api.patch(`/myroast/maintenance/tasks/${task.id}`, payload);
      } else {
        await api.post("/myroast/maintenance/tasks", payload);
      }
      onSaved();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4" data-testid="task-editor-modal">
      <div className="bg-card border border-border rounded shadow-xl max-w-lg w-full">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 className="font-display text-lg font-bold">{task ? "Edit" : "New"} task</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="task-editor-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={onSubmit} className="p-5 space-y-4">
          <Field label="Name *">
            <input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="task-input-name"
              placeholder="e.g. Clean drum"
              className={INPUT_CLS}
            />
          </Field>
          <Field label="Description">
            <textarea
              rows={2}
              value={form.description || ""}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              data-testid="task-input-description"
              placeholder="What needs to happen, and how thoroughly"
              className={INPUT_CLS}
            />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Category">
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                data-testid="task-input-category"
                className={INPUT_CLS}
              >
                {CATEGORY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Frequency">
              <select
                value={form.frequency_kind}
                onChange={(e) => setForm({ ...form, frequency_kind: e.target.value })}
                data-testid="task-input-frequency-kind"
                className={INPUT_CLS}
              >
                {FREQUENCY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </Field>
          </div>
          {usesValue && (
            <Field label={form.frequency_kind === "every_n_roasts" ? "Every N roasts" : "Every N hours"}>
              <input
                type="number"
                min="1"
                value={form.frequency_value}
                onChange={(e) => setForm({ ...form, frequency_value: e.target.value })}
                data-testid="task-input-frequency-value"
                placeholder="e.g. 50"
                className={INPUT_CLS}
              />
            </Field>
          )}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Estimated time (min)">
              <input
                type="number"
                min="0"
                value={form.estimated_minutes}
                onChange={(e) => setForm({ ...form, estimated_minutes: e.target.value })}
                data-testid="task-input-estimated"
                className={INPUT_CLS}
              />
            </Field>
            <Field label="Applies to">
              <select
                value={form.applies_to_machine_id || ""}
                onChange={(e) => setForm({ ...form, applies_to_machine_id: e.target.value })}
                data-testid="task-input-machine"
                className={INPUT_CLS}
              >
                <option value="">All machines</option>
                {machines.map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </Field>
          </div>
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              data-testid="task-input-active"
            />
            Active (operators see it on the tablet)
          </label>

          {error && <ErrorBanner message={error} />}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-border rounded text-sm hover:bg-secondary">Cancel</button>
            <button
              type="submit"
              disabled={submitting || !form.name.trim()}
              data-testid="task-editor-submit"
              className="px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-semibold hover:bg-[hsl(20,41%,32%)] disabled:opacity-60"
            >
              {submitting ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Logs tab
// ─────────────────────────────────────────────────────────────
function LogsPane({ isOwner, machines, operators, machineNameById, operatorNameById }) {
  const [tasks, setTasks] = useState([]);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const perPage = 50;

  const [filters, setFilters] = useState({
    machine_id: "",
    task_id: "",
    operator_id: "",
    since: defaultSince(),
    until: "",
  });

  // Load task catalogue once for the filter dropdown + row labels
  useEffect(() => {
    api.get("/myroast/maintenance/tasks", { params: { include_inactive: true } })
      .then((r) => setTasks(r.data || []))
      .catch(() => {});
  }, []);

  const taskNameById = useMemo(() => {
    const m = {};
    tasks.forEach((t) => { m[t.id] = t.name; });
    return m;
  }, [tasks]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const params = { page, per_page: perPage };
    if (filters.machine_id) params.machine_id = filters.machine_id;
    if (filters.task_id) params.task_id = filters.task_id;
    if (filters.operator_id) params.operator_id = filters.operator_id;
    if (filters.since) params.since = new Date(filters.since).toISOString();
    if (filters.until) params.until = new Date(filters.until + "T23:59:59").toISOString();
    try {
      const { data } = await api.get("/myroast/maintenance/logs", { params });
      setLogs(data.items || []);
      setTotal(data.total ?? 0);
      setHasMore(!!data.has_more);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);

  const onFilter = (k, v) => { setFilters({ ...filters, [k]: v }); setPage(1); };

  const onDelete = async (lg) => {
    if (!window.confirm("Remove this log entry? This is for correcting mis-entries; it doesn't undo the actual maintenance.")) return;
    try {
      await api.delete(`/myroast/maintenance/logs/${lg.id}`);
      await load();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <>
      <div className="bg-card border border-border rounded p-4 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <span className="label-tiny">Filters</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <FilterSelect
            label="Machine" value={filters.machine_id} onChange={(v) => onFilter("machine_id", v)}
            options={machines.map((m) => ({ value: m.id, label: m.name }))}
            testId="logs-filter-machine"
          />
          <FilterSelect
            label="Task" value={filters.task_id} onChange={(v) => onFilter("task_id", v)}
            options={tasks.map((t) => ({ value: t.id, label: t.name }))}
            testId="logs-filter-task"
          />
          <FilterSelect
            label="Operator" value={filters.operator_id} onChange={(v) => onFilter("operator_id", v)}
            options={operators.map((o) => ({
              value: o.id,
              label: operatorNameById[o.id] || o.name,
            }))}
            testId="logs-filter-operator"
          />
          <FilterDate label="From" value={filters.since} onChange={(v) => onFilter("since", v)} testId="logs-filter-since" />
          <FilterDate label="To" value={filters.until} onChange={(v) => onFilter("until", v)} testId="logs-filter-until" />
        </div>
      </div>

      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-muted-foreground">
          {loading ? "Loading…" : `${total} log${total === 1 ? "" : "s"} in window`}
        </p>
      </div>

      {error && <ErrorBanner message={error} />}

      <div className="bg-card border border-border rounded overflow-x-auto">
        <table className="w-full text-sm" data-testid="maintenance-logs-table">
          <thead>
            <tr className="bg-secondary">
              <th className="label-tiny px-4 py-3 text-left">When</th>
              <th className="label-tiny px-4 py-3 text-left">Task</th>
              <th className="label-tiny px-4 py-3 text-left">Machine</th>
              <th className="label-tiny px-4 py-3 text-left">Operator</th>
              <th className="label-tiny px-4 py-3 text-left">Notes</th>
              <th className="label-tiny px-4 py-3 text-right">Duration</th>
              {isOwner && <th className="w-12" />}
            </tr>
          </thead>
          <tbody>
            {!loading && logs.length === 0 && (
              <tr>
                <td colSpan={isOwner ? 7 : 6} className="px-4 py-12 text-center text-muted-foreground">
                  <p className="mb-1">No log entries in this window.</p>
                  <p className="text-xs">Operators record completions from the tablet&apos;s maintenance screen.</p>
                </td>
              </tr>
            )}
            {logs.map((lg) => (
              <tr key={lg.id} className="border-t border-border hover:bg-secondary/40" data-testid={`log-row-${lg.id}`}>
                <td className="px-4 py-2.5 whitespace-nowrap">{formatDateTime(lg.completed_at)}</td>
                <td className="px-4 py-2.5">{taskNameById[lg.task_id] || <em className="text-muted-foreground">deleted task</em>}</td>
                <td className="px-4 py-2.5">{machineNameById[lg.machine_id] || "—"}</td>
                <td className="px-4 py-2.5">{operatorNameById[lg.operator_id] || "—"}</td>
                <td className="px-4 py-2.5 text-muted-foreground">{lg.notes || ""}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">{lg.duration_minutes ? `${lg.duration_minutes} min` : "—"}</td>
                {isOwner && (
                  <td className="px-2 py-2 text-right">
                    <button
                      onClick={() => onDelete(lg)}
                      data-testid={`log-delete-${lg.id}`}
                      className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                      title="Delete log entry"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(hasMore || page > 1) && (
        <div className="flex items-center justify-between mt-3 text-sm">
          <button
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
            data-testid="logs-prev-page"
            className="px-3 py-1.5 border border-border rounded hover:bg-secondary disabled:opacity-40"
          >Previous</button>
          <span className="text-muted-foreground">Page {page}</span>
          <button
            disabled={!hasMore}
            onClick={() => setPage(page + 1)}
            data-testid="logs-next-page"
            className="px-3 py-1.5 border border-border rounded hover:bg-secondary disabled:opacity-40"
          >Next</button>
        </div>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// PDF report download dropdown
// ─────────────────────────────────────────────────────────────
function DownloadReport({ machines }) {
  const [open, setOpen] = useState(false);
  const [machineId, setMachineId] = useState("");
  const [since, setSince] = useState(defaultSince());
  const [until, setUntil] = useState("");

  const onDownload = () => {
    const url = new URL(`${process.env.REACT_APP_BACKEND_URL}/api/myroast/maintenance/report.pdf`);
    if (machineId) url.searchParams.set("machine_id", machineId);
    if (since) url.searchParams.set("since", new Date(since).toISOString());
    if (until) url.searchParams.set("until", new Date(until + "T23:59:59").toISOString());
    // Cookie auth carries via the new tab
    window.open(url.toString(), "_blank", "noopener,noreferrer");
    setOpen(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="maintenance-download-pdf-btn"
        className="inline-flex items-center gap-2 px-4 py-2 border border-border rounded text-sm font-medium hover:bg-secondary"
      >
        <FileDown className="w-4 h-4" /> Download PDF report
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 z-40 w-72 bg-card border border-border rounded shadow-xl p-4 space-y-3" data-testid="maintenance-pdf-popover">
            <Field label="Machine">
              <select
                value={machineId}
                onChange={(e) => setMachineId(e.target.value)}
                data-testid="pdf-machine"
                className={INPUT_CLS}
              >
                <option value="">All machines</option>
                {machines.map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </Field>
            <Field label="From">
              <input type="date" value={since} onChange={(e) => setSince(e.target.value)} data-testid="pdf-since" className={INPUT_CLS} />
            </Field>
            <Field label="To">
              <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} data-testid="pdf-until" className={INPUT_CLS} />
            </Field>
            <button
              onClick={onDownload}
              data-testid="maintenance-pdf-download-confirm"
              className="w-full px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-semibold hover:bg-[hsl(20,41%,32%)]"
            >
              <FileDown className="w-4 h-4 inline mr-1.5" /> Download
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Small shared helpers
// ─────────────────────────────────────────────────────────────
const INPUT_CLS = "w-full px-3 py-2 bg-white border border-border rounded text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary";

function Field({ label, children }) {
  return (
    <div>
      <label className="label-tiny mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="label-tiny mb-1 block">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} className={INPUT_CLS}>
        <option value="">All</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

function FilterDate({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="label-tiny mb-1 block">{label}</label>
      <input type="date" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} className={INPUT_CLS} />
    </div>
  );
}

function ErrorBanner({ message }) {
  return (
    <div className="flex items-start gap-2 bg-destructive/10 border border-destructive/30 text-destructive rounded p-3 text-sm mb-4">
      <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
      <span>{message}</span>
    </div>
  );
}

// Default the "From" filter to ~30 days ago so the page loads with
// useful content rather than the entire history.
function defaultSince() {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
}
